import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ActiveOrangeSwitch from '../../../../common/Switch';
import SearchBox from '../../../../common/SearchBox';
import ButtonScogoPrimary from '../../../../common/Buttons/ButtonScogoPrimary';
import ButtonScogoClosedOutlined from '../../../../common/Buttons/ButtonScogoClosedOutlined';
import { ColumnLoader } from '../../../team/components/SP/SPTable';
import LoadingDots from '../../../../common/ThreedotLoader';
import { closeModalAction } from '../../../../actions/modal';
import { getAutoAcceptProjectList, clearAutoAcceptProjectList } from '../../../../actions/project';
import { toggleAutoAccept, getAutoAcceptProjects, clearAutoAcceptProjects } from '../../../../actions/users';
import { categoryDropdownList } from '../../../project/AddProject/Form';
import { debounce } from '../../../../utils/common';

const formName = 'auto_accept_form';

// "All" sentinel option (value null) maps to the no-category / all-projects API call.
// Prepended to the category list and pre-selected by default so the modal opens
// showing every project (matching the existing default of fetching the full list).
const ALL_CATEGORY_OPTION = { value: null, key: '__ALL__', label: 'All' };
const categoryOptions = [ALL_CATEGORY_OPTION, ...categoryDropdownList];

export default function AutoAccept({ user }) {
    const dispatch = useDispatch();
    const [enabled, setEnabled] = useState(Boolean(user?.auto_accept));
    // `searchTerm` drives the input; `debouncedQuery` is what actually hits the API.
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    // Single-select category filter: a category value, or null for the pre-selected "All".
    const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORY_OPTION.value);
    // id -> project name, so selected rows keep their label even when filtered out of the list.
    const [selectedProjects, setSelectedProjects] = useState({});
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);

    // Paginated project list for the active category + search (server-side, infinite scroll).
    const projects = useSelector((state) => state.project.autoAcceptProjectList);
    const projectListPage = useSelector((state) => state.project.autoAcceptProjectListPage);
    const projectListHasMore = useSelector((state) => state.project.autoAcceptProjectListHasMore);
    const isProjectListLoading = useSelector((state) => state.loading.isAutoAcceptProjectListLoading);

    // Already-selected projects for this user, fetched once from getUserById and kept in the store.
    const autoAcceptProjects = useSelector((state) => state.user.autoAcceptProjects);
    const isAutoAcceptProjectsLoading = useSelector((state) => state.loading.isAutoAcceptProjectsLoading);

    // Guards the one-time prefill so it never clobbers the user's in-progress edits.
    const hasPrefilledRef = useRef(false);

    // Tracks whether the in-flight request appends a page (infinite scroll) or replaces
    // the list (search / category change), so each shows the right loader.
    const pendingAppendRef = useRef(false);

    // Fetch the user's existing auto-accept projects once per user; clear on unmount.
    // This is independent of the category filter, so changing category never refetches it.
    useEffect(() => {
        // Reset the prefill guard so a different user re-prefills from fresh data.
        hasPrefilledRef.current = false;
        if (user?.id) dispatch(getAutoAcceptProjects({ userId: user.id }));
        return () => { dispatch(clearAutoAcceptProjects()); };
    }, [dispatch, user?.id]);

    // Prefill the selected-project map once the fetched list arrives.
    useEffect(() => {
        if (hasPrefilledRef.current || !Array.isArray(autoAcceptProjects)) return;
        hasPrefilledRef.current = true;
        if (autoAcceptProjects.length === 0) return;
        const prefilled = {};
        autoAcceptProjects.forEach((project) => {
            if (project && project.id !== undefined && project.id !== null) {
                prefilled[project.id] = project.name;
            }
        });
        setSelectedProjects((prev) => ({ ...prefilled, ...prev }));
    }, [autoAcceptProjects]);

    // Fetch the first page whenever the section is enabled or the category/search changes.
    // This replaces the list (append = false); the search query is sent to the API.
    useEffect(() => {
        if (!enabled) return;
        pendingAppendRef.current = false;
        dispatch(getAutoAcceptProjectList({ category: selectedCategory, query: debouncedQuery, page: 1, append: false }));
    }, [dispatch, enabled, selectedCategory, debouncedQuery]);

    // Clear the list on unmount so reopening the modal starts fresh.
    useEffect(() => () => { dispatch(clearAutoAcceptProjectList()); }, [dispatch]);

    // Debounce the API search so we fire one request after typing settles, not per keystroke.
    const debouncedSetQuery = useMemo(() => debounce((value) => setDebouncedQuery(value)), []);

    const onSearchChange = (event) => {
        const value = event?.target?.value ?? '';
        setSearchTerm(value);
        debouncedSetQuery(value.trim());
    };

    const onSearchCancel = () => {
        setSearchTerm('');
        setDebouncedQuery('');
    };

    // Infinite scroll: fetch the next page near the bottom, unless a request is already
    // in flight or the last page has been reached.
    const loadMore = () => {
        if (!enabled || isProjectListLoading || !projectListHasMore) return;
        pendingAppendRef.current = true;
        dispatch(getAutoAcceptProjectList({ category: selectedCategory, query: debouncedQuery, page: projectListPage + 1, append: true }));
    };

    const handleProjectsScroll = (event) => {
        const el = event.target;
        const reachedBottom = Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) < 24;
        if (reachedBottom) loadMore();
    };

    // Single-select: clicking a category selects it; clicking the active one falls back to "All".
    // Changing the category only updates state; the effect above refetches page 1 for it.
    const onSelectCategory = (value) => {
        setSelectedCategory((prev) => (prev === value ? ALL_CATEGORY_OPTION.value : value));
    };

    const toggleProject = (project) => {
        setSelectedProjects((prev) => {
            const next = { ...prev };
            if (next[project.id]) delete next[project.id];
            else next[project.id] = project.name;
            return next;
        });
    };

    const removeProject = (projectId) => {
        setSelectedProjects((prev) => {
            const next = { ...prev };
            delete next[projectId];
            return next;
        });
    };

    const clearAll = () => setSelectedProjects({});

    const projectList = Array.isArray(projects) ? projects : [];
    // A query the user has typed but that hasn't been fetched yet (debounce window).
    // Derived, so it self-corrects and can never get stuck showing the loader.
    const isSearchPending = searchTerm.trim() !== debouncedQuery;
    // Replace-type load (search / category / initial) shows the full-panel loader and hides
    // stale results; an infinite-scroll append shows a bottom spinner under the existing list.
    const isReplacing = isSearchPending || (isProjectListLoading && !pendingAppendRef.current);
    const isLoadingMore = isProjectListLoading && pendingAppendRef.current;

    const selectedIds = Object.keys(selectedProjects);
    const selectedCount = selectedIds.length;

    // Block submit until the existing selection has been prefilled, otherwise a fast
    // submit could overwrite the user's saved projects with an empty list.
    const isPrefilling = enabled && isAutoAcceptProjectsLoading && !hasPrefilledRef.current;

    const onSubmit = () => {
        if (isPrefilling) return;
        const payload = {
            auto_accept: enabled,
            project_ids: enabled ? selectedIds.map((id) => (Number.isNaN(Number(id)) ? id : Number(id))) : [],
        };
        dispatch(toggleAutoAccept({ formName, payload, userId: user.id }));
    };

    const isSubmitting = Boolean(formButtonLoading[formName]);

    return (
        <div className='flex flex-col bg-white' style={{ height: enabled ? '78vh' : 'auto', maxHeight: '80vh' }}>
            <style>{`
                @keyframes aaSearchBorderRun {
                    0% { background-position: 0% 50%; }
                    100% { background-position: 200% 50%; }
                }
                .aa-search-glow {
                    background-image: linear-gradient(90deg, #ff6b00, #ffb800, #ff3d71, #7b61ff, #ff6b00);
                    background-size: 200% 100%;
                    animation: aaSearchBorderRun 3s linear infinite;
                }
            `}</style>
            {/* Toggle card (fixed) */}
            <div className={`px-8 pt-6 shrink-0 ${enabled ? 'pb-0' : 'pb-6'}`}>
                <div className='flex items-center justify-between gap-4 p-4 rounded-lg border border-scogoddd bg-scogof8'>
                    <div>
                        <p className='text-font13 font-medium text-scogo15'>Enable Auto Accept</p>
                        <p className='text-font11 text-scogo99 mt-1'>
                            When enabled, tickets from the selected projects are auto-accepted for this user.
                        </p>
                    </div>
                    <div className='flex items-center gap-2 shrink-0'>
                        <span className={`text-font11 font-medium ${enabled ? 'text-scogoorange' : 'text-scogo99'}`}>
                            {enabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <ActiveOrangeSwitch isChecked={enabled} onChange={() => setEnabled((prev) => !prev)} />
                    </div>
                </div>
            </div>

            {enabled && (
                <>
                    {/* Two columns: category (left) + projects (right) */}
                    <div className='flex-1 min-h-0 px-8 pt-5 pb-2 flex gap-5'>
                        {/* Left: category selector */}
                        <div className='flex flex-col min-h-0' style={{ width: '25%' }}>
                            <p className='text-font12 font-medium text-scogo15 mb-1'>Select Category</p>
                            <p className='text-font10 text-scogo99 mb-2'>Filter projects by category</p>
                            <div className='flex-1 min-h-0 overflow-y-auto pr-1 flex flex-col gap-2'>
                                {categoryOptions.map((cat) => {
                                    const active = selectedCategory === cat.value;
                                    return (
                                        <button
                                            key={cat.key}
                                            type='button'
                                            onClick={() => onSelectCategory(cat.value)}
                                            className={`flex items-center justify-center gap-1 px-3 py-2 rounded-md text-font11 font-medium transition-colors ${active ? 'bg-scogoorange text-white' : 'bg-white text-scogo15 border border-scogoddd hover:bg-scogof8'}`}
                                        >
                                            {active && <span className='material-icons text-font14'>check</span>}
                                            {cat.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Right: project search + add list */}
                        <div className='flex flex-col min-h-0 flex-1'>
                            <p className='text-font12 font-medium text-scogo15 mb-2'>Add Projects</p>
                            <div className='aa-search-glow rounded-md p-[2px]'>
                                <SearchBox
                                    searchTerm={searchTerm}
                                    onChange={onSearchChange}
                                    onCancel={onSearchCancel}
                                    placeholder='Search projects by name...'
                                    isExpandingEnabled={false}
                                    inputWidth='w-full'
                                    className='w-full'
                                    inputClass='border-none focus:ring-0 bg-white placeholder-scogo99 rounded-[5px]'
                                />
                            </div>
                            <div className='flex-1 min-h-0 mt-3 rounded-lg border border-scogoddd overflow-hidden'>
                                <div className='h-full overflow-y-auto' onScroll={handleProjectsScroll}>
                                    {isReplacing ? (
                                        <div className='p-6 flex justify-center'><ColumnLoader /></div>
                                    ) : projectList.length === 0 ? (
                                        <div className='p-6 text-center text-scogo99 text-font12'>No projects found</div>
                                    ) : (
                                        <>
                                            <div className='flex flex-wrap gap-2 p-3'>
                                                {projectList.map((project) => {
                                                    const checked = Boolean(selectedProjects[project.id]);
                                                    return (
                                                        <span
                                                            key={project.id}
                                                            role='button'
                                                            onClick={() => toggleProject(project)}
                                                            className={`inline-flex items-center gap-1 pl-3 pr-2 py-1 rounded-full text-font11 cursor-pointer transition-colors border ${checked ? 'text-scogoorange bg-white border-scogoorange' : 'text-scogo15 bg-scogof8 border-scogoddd hover:bg-white'}`}
                                                        >
                                                            {project.name}
                                                            <span className={`material-icons text-font14 ${checked ? 'text-scogoorange' : 'text-scogo99'}`}>
                                                                {checked ? 'check_circle' : 'add_circle_outline'}
                                                            </span>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                            {isLoadingMore && (
                                                <div className='pb-3 flex justify-center'><LoadingDots /></div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Selected projects (fixed, after selection) */}
                    <div className='px-8 pt-4 pb-1 shrink-0 border-t border-scogoeee'>
                        <div className='flex items-center justify-between mb-2'>
                            <p className='text-font12 font-medium text-scogo15 flex items-center'>
                                Selected Projects
                                <span className='ml-2 inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-font09 text-white bg-scogoorange'>
                                    {selectedCount}
                                </span>
                                {isPrefilling && (
                                    <span className='ml-2 text-font10 font-normal text-scogo99'>Loading saved projects…</span>
                                )}
                            </p>
                            {selectedCount > 0 && (
                                <button type='button' onClick={clearAll} className='text-font11 text-scogoclosed hover:underline'>
                                    Clear all
                                </button>
                            )}
                        </div>

                        {selectedCount > 0 ? (
                            <div className='flex flex-wrap gap-2 p-3 rounded-lg border border-scogoddd bg-scogof8 overflow-y-auto' style={{ maxHeight: '96px' }}>
                                {selectedIds.map((id) => (
                                    <span
                                        key={id}
                                        className='inline-flex items-center gap-1 pl-3 pr-2 py-1 rounded-full text-font11 text-scogoorange bg-white border border-scogoorange'
                                    >
                                        {selectedProjects[id]}
                                        <span
                                            role='button'
                                            onClick={() => removeProject(id)}
                                            className='material-icons text-font14 cursor-pointer hover:text-scogoclosed'
                                        >
                                            close
                                        </span>
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className='text-font11 text-scogo99'>No projects selected yet.</p>
                        )}
                    </div>
                </>
            )}

            {/* Footer (fixed) */}
            <div className='flex gap-3 justify-end px-8 py-4 border-t border-scogoeee bg-white shrink-0'>
                <ButtonScogoClosedOutlined disabled={isSubmitting} textOrComponent='Cancel' onClick={() => dispatch(closeModalAction())} />
                <ButtonScogoPrimary loading={isSubmitting} disabled={isPrefilling} textOrComponent='Submit' onClick={onSubmit} />
            </div>
        </div>
    );
}
